import { unifiedClient } from '@/lib/unified-fetch';

/**
 * Get a specific cookie value by name
 * @param name - The cookie name to retrieve
 * @returns The cookie value or null if not found
 */
export function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const cookieValue = parts.pop()?.split(';').shift();
    return cookieValue || null;
  }
  return null;
}

/**
 * Get the ss_admin cookie specifically
 * @returns The ss_admin cookie value or null if not found
 */
export function getAdminCookie(): string | null {
  return getCookie('ss_admin');
}

/**
 * Check if admin cookie exists and return its info
 * @returns Object with cookie status and value info
 */
export function getAdminCookieInfo(): {
  exists: boolean;
  value: string | null;
  length: number;
  preview: string | null;
} {
  const cookieValue = getAdminCookie();
  return {
    exists: !!cookieValue,
    value: cookieValue,
    length: cookieValue?.length || 0,
    preview: cookieValue ? `${cookieValue.substring(0, 20)}...` : null,
  };
}

/**
 * User service functions using unified client
 */

/**
 * Fetch current user information from /api/app/me
 * @returns Promise<API.CurrentUser>
 */
export async function fetchCurrentUser(): Promise<API.CurrentUser> {
  const response = await unifiedClient.app.GET('/me');
  
  const { data, error } = response;
  
  if (error) {
    throw new Error(`Failed to fetch user info: ${String(error)}`);
  }
  
  if (!data) {
    throw new Error('No user data received');
  }
  
  return data;
}

/**
 * Fetch current user information with error handling
 * @param options - Additional options for error handling
 * @returns Promise<API.CurrentUser | null>
 */
export async function fetchCurrentUserSafe(options?: { 
  skipErrorHandler?: boolean 
}): Promise<API.CurrentUser | null> {
  try {
    return await fetchCurrentUser();
  } catch (error) {
    if (!options?.skipErrorHandler) {
      console.error('Error fetching current user:', error);
    }
    return null;
  }
}

/**
 * Check if user is authenticated by attempting to fetch user info
 * @returns Promise<boolean>
 */
export async function checkUserAuthentication(): Promise<boolean> {
  try {
    const user = await fetchCurrentUser();
    return !!user && !!user.id;
  } catch {
    return false;
  }
}